import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Machines.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';

const MachinesList = forwardRef(({ onSelectMachine, selectedMachineId, onNew }, ref) => {
  const { t } = useTranslation('production');
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchMachines
  }));

  useEffect(() => {
    fetchMachines();
  }, [typeFilter, statusFilter]);

  const fetchMachines = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/machines?page_size=100'));
      if (response.ok) {
        const data = await response.json();
        setMachines(Array.isArray(data) ? data : []);
      } else {
        setError(t('machines.list.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching machines:', error);
      setError(t('machines.list.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredMachines = machines.filter(machine => {
    const matchesSearch = (machine.machine_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          machine.machine_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          machine.location?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType = typeFilter === 'all' || machine.machine_type?.toUpperCase() === typeFilter.toUpperCase();

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && machine.status?.toUpperCase() === 'ACTIVE') ||
                         (statusFilter === 'maintenance' && machine.status?.toUpperCase() === 'MAINTENANCE') ||
                         (statusFilter === 'inactive' && machine.status?.toUpperCase() === 'INACTIVE') ||
                         (statusFilter === 'broken' && machine.status?.toUpperCase() === 'BROKEN');

    return matchesSearch && matchesType && matchesStatus;
  });

  const getTypeBadge = (machineType) => {
    const typeUpper = machineType?.toUpperCase();
    let className = 'type-badge ';

    switch (typeUpper) {
      case 'PRINTING':
        className += 'type-printing';
        break;
      case 'CUTTING':
        className += 'type-cutting';
        break;
      case 'FOLDING':
        className += 'type-folding';
        break;
      case 'LAMINATING':
        className += 'type-laminating';
        break;
      case 'PACKAGING':
        className += 'type-packaging';
        break;
      default:
        className += 'type-other';
    }

    return <span className={className}>{machineType || t('machines.list.types.other')}</span>;
  };

  const getStatusBadge = (status) => {
    const statusUpper = status?.toUpperCase();
    let className = 'status-badge ';

    switch (statusUpper) {
      case 'ACTIVE':
        className += 'status-machine-active';
        break;
      case 'MAINTENANCE':
        className += 'status-machine-maintenance';
        break;
      case 'INACTIVE':
        className += 'status-machine-inactive';
        break;
      case 'BROKEN':
        className += 'status-machine-broken';
        break;
      default:
        className += 'status-machine-unknown';
    }

    // Display status text with translations
    let displayText;
    switch (statusUpper) {
      case 'ACTIVE':
        displayText = t('machines.list.status.active');
        break;
      case 'MAINTENANCE':
        displayText = t('machines.list.status.maintenance');
        break;
      case 'INACTIVE':
        displayText = t('machines.list.status.inactive');
        break;
      case 'BROKEN':
        displayText = t('machines.list.status.broken');
        break;
      default:
        displayText = status?.charAt(0).toUpperCase() + status?.slice(1).toLowerCase() || t('machines.list.status.active');
    }
    return <span className={className}>{displayText}</span>;
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && machines.length === 0) {
    return <div className="list-container"><div className="loading">{t('machines.list.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('machines.list.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('machines.list.newMachine')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('machines.list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('machines.list.filters.allTypes')}</option>
            <option value="printing">{t('machines.list.types.printing')}</option>
            <option value="cutting">{t('machines.list.types.cutting')}</option>
            <option value="folding">{t('machines.list.types.folding')}</option>
            <option value="laminating">{t('machines.list.types.laminating')}</option>
            <option value="packaging">{t('machines.list.types.packaging')}</option>
            <option value="other">{t('machines.list.types.other')}</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('machines.list.filters.allStatus')}</option>
            <option value="active">{t('machines.list.status.active')}</option>
            <option value="maintenance">{t('machines.list.status.maintenance')}</option>
            <option value="inactive">{t('machines.list.status.inactive')}</option>
            <option value="broken">{t('machines.list.status.broken')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('machines.list.headers.code')}</th>
              <th>{t('machines.list.headers.machineName')}</th>
              <th>{t('machines.list.headers.type')}</th>
              <th>{t('machines.list.headers.status')}</th>
              <th>{t('machines.list.headers.location')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredMachines.length === 0 ? (
              <tr>
                <td colSpan="5" className="no-data">
                  {searchTerm ? t('machines.list.noData.noResults') : t('machines.list.noData.noMachines')}
                </td>
              </tr>
            ) : (
              filteredMachines.map(machine => (
                <tr
                  key={machine.machine_id}
                  onClick={() => onSelectMachine(machine.machine_id)}
                  className={selectedMachineId === machine.machine_id ? 'selected' : ''}
                >
                  <td className="machine-code">
                    <code>{machine.machine_code || t('machines.list.defaultValues.na')}</code>
                  </td>
                  <td className="machine-info">
                    <div className="machine-name">
                      <strong>{machine.machine_name || t('machines.list.defaultValues.unnamedMachine')}</strong>
                    </div>
                    {machine.manufacturer && machine.model &&
                     !machine.machine_name?.includes(machine.manufacturer) &&
                     !machine.machine_name?.includes(machine.model) && (
                      <div className="machine-details">{machine.manufacturer} {machine.model}</div>
                    )}
                  </td>
                  <td>
                    {getTypeBadge(machine.machine_type)}
                  </td>
                  <td>
                    {getStatusBadge(machine.status)}
                  </td>
                  <td className="machine-location">
                    {machine.location || t('machines.list.defaultValues.notSpecified')}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('machines.list.footer.recordCount', { filtered: filteredMachines.length, total: machines.length })}
        </div>
      </div>
    </div>
  );
});

MachinesList.displayName = 'MachinesList';

export default MachinesList;